import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { roleMatchesAny, deriveDepartment } from '../constants/roles';
import { registerTokenRefresher, unregisterTokenRefresher } from '../services/api/tokenRefresh.js';

export const AuthContext = createContext(null);

// Get environment variables
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID;
const authority = import.meta.env.VITE_ENTRA_AUTHORITY;
const redirectUri = import.meta.env.VITE_REDIRECT_URI;

// Request token for the ODIN1 Dashboard application itself
// This ensures the token audience matches our app, not Microsoft Graph
const loginScopes = [
  `${clientId}/.default`,  // Request all permissions configured for this app
];

// MSAL configuration
const msalConfig = {
  auth: {
    clientId: clientId || 'placeholder-client-id',
    authority: authority || 'https://login.microsoftonline.com/common',
    redirectUri: redirectUri || window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false, // Fixed: Works on HTTP without Secure cookies
  },
};

// Only log in development
if (import.meta.env.DEV) {
  console.log('MSAL Config:', {
    clientId: clientId ? '***configured***' : 'not configured',
    authority: msalConfig.auth.authority,
    redirectUri: msalConfig.auth.redirectUri,
  });
}

export const AuthProvider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [msalInstance] = useState(() => {
    return new PublicClientApplication(msalConfig);
  });

  // Initialize MSAL instance on component mount
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();
        setIsInitialized(true);

        // Handle the response from auth redirect after initialization
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
          setAccount(response.account);
          localStorage.setItem('accessToken', response.accessToken);
        } else {
          // Check for existing accounts
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            setAccount(accounts[0]);
            // Try to get a fresh token silently
            try {
              const silentResult = await msalInstance.acquireTokenSilent({
                scopes: loginScopes,
                account: accounts[0],
              });
              localStorage.setItem('accessToken', silentResult.accessToken);
            } catch (silentError) {
              console.log('Silent token acquisition failed, user may need to re-login');
            }
          }
        }
      } catch (err) {
        console.error('Error during MSAL initialization:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeMsal();
  }, [msalInstance]);

  // Bridge for non-React callers (gateway.js's 401 refresh-and-retry, the
  // walk-round pre-batch refresh): a SILENT-only refresher. An
  // interaction-required failure (e.g. AADSTS53003 — Conditional Access
  // blocking token issuance, the 2026-08-20 walk-round incident) rejects, and
  // the bridge resolves it to null so background code paths never launch
  // interactive auth themselves — the existing 401 fallback handles that.
  useEffect(() => {
    if (!account || !isInitialized) return undefined;
    registerTokenRefresher(async () => {
      const result = await msalInstance.acquireTokenSilent({
        scopes: loginScopes,
        account,
      });
      localStorage.setItem('accessToken', result.accessToken);
      return result.accessToken;
    });
    return () => unregisterTokenRefresher();
  }, [account, msalInstance, isInitialized]);

  const getAccessToken = useCallback(async () => {
    if (!account || !msalInstance || !isInitialized) {
      throw new Error('User not authenticated or MSAL not initialized');
    }

    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        scopes: loginScopes,
        account: account,
      });

      localStorage.setItem('accessToken', tokenResponse.accessToken);
      return tokenResponse.accessToken;
    } catch (error) {
      console.error('Failed to acquire token silently:', error);

      if (error instanceof InteractionRequiredAuthError) {
        // Redirect flow only (house rule 4) — popups are blocked on the Android
        // field fleet, which is where interaction-required surfaces in practice
        // (2026-08-20 walk-round 53003 incident). The redirect navigates away;
        // the throw below never runs but keeps the contract explicit.
        await msalInstance.acquireTokenRedirect({
          scopes: loginScopes,
          account: account,
        });
      }

      throw error;
    }
  }, [account, msalInstance, isInitialized]);

  const login = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);

      if (!isInitialized) {
        throw new Error('MSAL is not initialized yet. Please wait and try again.');
      }

      // Clear any existing tokens
      localStorage.removeItem('accessToken');

      const loginRequest = {
        scopes: loginScopes,
        prompt: 'select_account',
      };

      // Use redirect flow instead of popup for better reliability
      await msalInstance.loginRedirect(loginRequest);

      // Note: The page will redirect, so code below won't execute
      // The response is handled in initializeMsal via handleRedirectPromise
    } catch (error) {
      console.error('Login failed:', error);
      setError(error);
      setIsLoading(false);
      throw error;
    }
  }, [msalInstance, isInitialized]);

  const logout = useCallback(async () => {
    try {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('userDepartment');
      setAccount(null);

      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        await msalInstance.logoutRedirect({
          account: accounts[0],
          postLogoutRedirectUri: window.location.origin,
        });
      }
    } catch (error) {
      console.error('Logout failed:', error);
      // Clear local state anyway
      setAccount(null);
      localStorage.removeItem('accessToken');
    }
  }, [msalInstance]);

  // Extract roles from ID token claims
  const extractRoles = (acc) => {
    const idTokenClaims = acc?.idTokenClaims || {};
    return idTokenClaims.roles || [];
  };

  const userRoles = extractRoles(account);

  // Department for the gateway header: 'admin' for Admin, 'members' for everyone else.
  const userDepartment = deriveDepartment(userRoles);
  if (account) {
    localStorage.setItem('userDepartment', userDepartment);
  }

  // Access checks: normalise the raw claim (unknown and retired roles drop out) then match.
  const hasRole = (role) => roleMatchesAny(userRoles, [role]);
  const hasAnyRole = (roles) => roleMatchesAny(userRoles, roles);

  const value = {
    account,
    error,
    isInitialized,
    isLoading,
    isAuthenticated: !!account,
    login,
    logout,
    getAccessToken,
    // Role-based access control
    userRoles,
    hasRole,
    hasAnyRole,
    userDepartment,
    // User info helpers
    user: account ? {
      name: account.name,
      email: account.username,
      tenantId: account.tenantId,
      roles: userRoles,
      localAccountId: account.localAccountId,   // OID — stable unique user ID
      homeAccountId: account.homeAccountId,     // oid.tid cross-tenant form
    } : null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
